// The far-left rail: the accounts this workspace publishes to, always on screen.
//
// It is deliberately the *outermost* column and deliberately not part of the
// sidebar, because the two answer different questions and only one of them is
// allowed to disappear. The menu collapses away to nothing; the rail does not,
// because which accounts exist is the context that makes everything else on
// screen mean something.
//
// That is also why the control that brings the menu back lives here, at the
// top, above the divider: when the menu is gone this column is the only thing
// left on that side of the window, so it has to carry the way back. The header
// keeps its toggle as well — the two drive the same state, and whichever one
// the hand is nearer to is the right one.
//
// It holds its own list, edited by its own ★ at the foot of the column. The top
// bar keeps a separate one: that bar is wide and labelled and suits screens,
// this is a column of 30px icons and suits the few things reached without
// looking, and one list forced into both shapes was a compromise in each.
//
// Compose is always the first item and cannot be removed. It is what the
// product is for, and a rail somebody had accidentally emptied of it would be a
// rail that had lost the point.
//
// Pressing an account opens the composer with it already ticked. That is the
// only thing "switching to an account" can honestly mean in this product: there
// is no per-account feed to switch into, because we never asked a network for
// one.

import { NavLink, useLocation } from "react-router-dom";
import { AccountMark } from "../components/AccountMark";
import { FavouritesMenu } from "./FavouritesMenu";
import { ConnectionsIcon, MenuIcon, PlusIcon } from "./icons";
import type { FavouritesState } from "./state";
import type { FavouriteItem } from "./state";

export function FavoritesRail({
  rail, accountsConnected, loading, navOpen, onToggleNav,
}: {
  rail: FavouritesState;
  /** Whether this workspace has any active account at all — a different
   *  question from whether one is pinned, now that a favourite can be a
   *  screen. It decides whether the button at the foot invites a first
   *  connection or manages existing ones. */
  accountsConnected: boolean;
  loading: boolean;
  navOpen: boolean;
  onToggleNav: () => void;
}) {
  const location = useLocation();
  // "Current" means: on this screen, or composing to this account right now.
  const composingTo = location.pathname === "/compose"
    ? new URLSearchParams(location.search).get("to")
    : null;
  const isCurrent = (item: FavouriteItem) =>
    item.connection
      ? composingTo === item.connection.id
      : location.pathname === item.route;
  const favourites = rail.favourites;

  return (
    <nav className="fav-rail" aria-label="Favourites">
      {/* Not inside the list below: this is not an account, and a screen reader
          walking "2 of 7 accounts" through a menu button would be told a lie
          about what the rail contains. */}
      <button
        type="button"
        className="fav-item fav-nav-toggle"
        onClick={onToggleNav}
        aria-expanded={navOpen}
        aria-controls="app-sidebar"
        aria-label={navOpen ? "Hide navigation" : "Show navigation"}
        data-tip={navOpen ? "Hide navigation" : "Show navigation"}
      >
        <MenuIcon className="nav-icon" />
      </button>
      <span className="fav-rail-rule" aria-hidden="true" />

      <ul className="fav-rail-list">
        {loading && favourites.length === 0 && (
          <>
            {/* Not a blank column while the list loads: three discs in the
                shape of the answer, so the rail does not jump into place. */}
            <li aria-hidden="true"><span className="fav-skeleton" /></li>
            <li aria-hidden="true"><span className="fav-skeleton" /></li>
          </>
        )}

        {favourites.map((item) => {
          const Icon = item.icon;
          const on = isCurrent(item);
          const name = item.connection
            ? `Compose to ${item.hint} — ${item.label}`
            : item.label;
          return (
            <li key={item.key}>
              <NavLink
                to={item.route}
                className={on ? "fav-item on" : "fav-item"}
                aria-current={on ? "page" : undefined}
                // No visible label in this column, so the accessible name has
                // to come from here. `title` keeps the long disclosure.
                aria-label={name}
                title={item.title}
                data-tip={name}
              >
                {item.connection
                  ? <AccountMark connection={item.connection} />
                  : Icon ? <Icon className="nav-icon" /> : null}
              </NavLink>
            </li>
          );
        })}

        <li>
          {/* This rail's own picker, at its foot rather than beside the plan
              chip: two stars in one place editing two different lists would be
              a puzzle. Each sits on the thing it changes. */}
          <FavouritesMenu
            candidates={rail.candidates}
            isFavourite={rail.isFavourite}
            toggle={rail.toggle}
            customised={rail.customised}
            reset={rail.reset}
            fixed={rail.fixed}
            count={favourites.length}
            surfaceName="rail"
            placement="beside"
          />
        </li>

        <li>
          <NavLink
            to="/connections"
            className="fav-item fav-add"
            aria-label={accountsConnected
              ? "Connect or manage accounts"
              : "Connect a social account"}
            data-tip={accountsConnected ? "Manage accounts" : "Connect an account"}
            title="Connect or disconnect a Facebook Page, Instagram professional account or YouTube channel. Shows exactly which permissions each account granted, and lets you revoke ours at any time."
          >
            {!accountsConnected && !loading
              ? <ConnectionsIcon className="nav-icon" />
              : <PlusIcon className="nav-icon" />}
          </NavLink>
        </li>
      </ul>
    </nav>
  );
}
